import React from 'react';
import useHttpGetHistoricalData from '../../hooks/devices/useHttpGetHistoricalData';
import Loader from '../../components/Loader';
import CanvasJSReact from '@canvasjs/react-charts';
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const HistoricalDataSafetyWrapper = ({ deviceId }) => {
    const { data, isReady } = useHttpGetHistoricalData(deviceId);
    if (!isReady) {
        return <Loader />;
    }

    return <HistoricalData samples={data?.data ?? []} />;
};

function HistoricalData({ samples }) {
    if (samples.length === 0) {
        return (
            <div>
                <h1>Historical Data for gateway</h1>
                <p>No samples found</p>
            </div>
        );
    }

    let chart = null;

    // helper to pick the right timestamp field
    const getTimestamp = sample => {
        const t = sample.group ?? sample.time ?? sample.timestamp;
        return t ? new Date(t) : new Date();
    };

    // map of plot key → extractor function sample => y
    const valueExtractors = {
        'Power Consumption (kW)': sample => sample.power_avg ?? sample.power,
        'Net Facility Power (kW)': sample => sample.sensors?.power_facility,
        'PV (Solar) Power (kW)': sample => sample.sensors?.power_solar,
        'Battery Power (kW)': sample => sample.sensors?.power_battery,
        'Battery State of Charge (%)': sample => sample.sensors?.soc,
        'Battery Power Setpoint (kW)': sample => sample.sensors?.power_setpoint,
        'Grid Frequency (Hz)': sample => sample.sensors?.frequency,
    };

    const plot_data = Object.entries(valueExtractors).map(([key, valueExtractor]) => ({
        type: 'line',
        name: key,
        showInLegend: true,
        xValueType: 'dateTime',
        visible: key === 'Power Consumption (kW)',
        dataPoints: samples.map(sample => ({
            x: getTimestamp(sample),
            y: valueExtractor?.(sample) ?? null,
        })),
    }));

    function toggleDataSeries(e) {
        if (typeof e.dataSeries.visible === 'undefined' || e.dataSeries.visible) {
            e.dataSeries.visible = false;
        } else {
            e.dataSeries.visible = true;
        }
        if (chart) {
            chart.render();
        }
    }

    const options = {
        zoomEnabled: true,
        animationEnabled: true,
        axisY: {
            lineThickness: 1,
        },
        legend: {
            cursor: 'pointer',
            itemclick: toggleDataSeries,
        },
        toolTip: {
            shared: false,
            contentFormatter: function (e) {
                let t = e.entries[0].dataPoint.x;
                let day = t.getDate();
                let month = t.getMonth() + 1;
                let hours = t.getHours().toString().padStart(2, '0');
                let minutes = t.getMinutes().toString().padStart(2, '0');
                return `${day}/${month} ${hours}:${minutes}: ${e.entries[0].dataPoint.y}`;
            },
        },
        data: plot_data,
    };

    return (
        <div>
            <h1>Historical Data for gateway</h1>

            <CanvasJSChart options={options} onRef={ref => (chart = ref)} />

            <HistoricalDataLegendSummary />
        </div>
    );
}

const HistoricalDataLegendSummary = () => {
    const items = [
        {
            label: 'Power Consumption (kW)',
            description: 'Total inverter power (PV + Battery) at a given moment. Consumption is positive,  production is negative.',
        },
        { label: 'Net Facility Power (kW)', description: 'Net import/export to the grid. Export is positive, import is negative.' },
        { label: 'PV (Solar) Power (kW)', description: 'Solar generation.' },
        { label: 'Battery Power (kW)', description: 'Battery power at a given moment. Charging (+) or discharging (–) from the battery.' },
        { label: 'Battery State of Charge (%)', description: 'Historical battery charge level.' },
        { label: 'Battery Power Setpoint (kW)', description: 'Target battery level defined by the active mode logic.' },
        {
            label: 'Grid Frequency (Hz)',
            description:
                'Measured grid frequency at the timestamp. This value is only relevant if there is a grid frequency meter installed at the customers site',
        },
    ];

    return (
        <div className="historical-data__legend-summary">
            <h5>Descriptions:</h5>
            <div className="historical-data__grid">
                {items.map((item, index) => (
                    <div key={index}>
                        <strong>{item.label}:</strong> {item.description}
                    </div>
                ))}
            </div>
        </div>
    );
};

export { HistoricalDataSafetyWrapper as HistoricalData };
